import { useAuth } from 'hooks/UseAuth'
import { useEffect } from 'react'
import { useRootStore } from 'stores/RootStore'
import { reportError } from 'utils/error'
import { crc32 } from './crc32'
import { EXPERIMENTS, ExperimentId } from './config'

const userPropertiesSet = new Set<number>()

const getAssignmentBucket = (userId: number, experimentId: string): number => {
  const hashValue = crc32(`${userId}-${experimentId}`)
  return hashValue % 100
}

const getExperimentVariant = (userId: number, experimentId: ExperimentId): string => {
  const config = EXPERIMENTS[experimentId]
  const bucket = getAssignmentBucket(userId, experimentId)
  let cumulative = 0
  for (const [variant, percentage] of Object.entries(config.allocation)) {
    cumulative += percentage
    if (bucket < cumulative) {
      return variant
    }
  }
  return config.variants[0]
}

export const getAllExperimentAssignments = (userId: number): Record<string, string> => {
  return Object.keys(EXPERIMENTS).reduce((assignments, experimentId) => {
    assignments[experimentId] = getExperimentVariant(userId, experimentId as ExperimentId)
    return assignments
  }, {} as Record<string, string>)
}

export const useExperiment = (experimentId: ExperimentId) => {
  const auth = useAuth()
  const rootStore = useRootStore()
  const config = EXPERIMENTS[experimentId]

  useEffect(() => {
    if (!auth?.id || userPropertiesSet.has(auth.id)) return

    const experimentAssignments = getAllExperimentAssignments(auth.id)
    const userProperties = Object.entries(experimentAssignments).reduce((props, [expId, expVariant]) => {
      if (expVariant) {
        props[`experiment_${expId}`] = expVariant
      }
      return props
    }, {} as Record<string, string>)

    if (Object.keys(userProperties).length > 0) {
      try {
        window.heap?.addUserProperties(userProperties)
        userPropertiesSet.add(auth.id)
      } catch (e) {
        reportError(e, {
          severity: 'error',
          metaData: { userProperties },
        })
      }
    }
  }, [auth?.id])

  if (!auth?.id) return null

  // optional to add feature flags based on experiment and variants, only need if we want to override
  const forcedVariant = config.variants.find((variant) =>
    rootStore.featureFlags.isActive(`experiment_${experimentId}_${variant}` as any),
  )

  return forcedVariant || getExperimentVariant(auth.id, experimentId)
}
